import { toast } from '@/components/toast';
import { maxImageSizeInBytes } from '@/environment';
import { removeMetadataAndCreateFile } from '@/hooks';
import { useGetBackgroundsList } from '@/hooks/useGetBackgroundsList';
import { useAnalytics } from '@/services/analytics';
import { uploadImage } from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { ERROR_KINDS, moderationError } from '@/state/layout/moderation';
import { annoynmousUserToEditorPagePostSsoReturnTo, bytesToMB, localFileToURL, renameFile } from '@/utils/utils';
import { ImageType } from '@d-id/types';
import { AxiosError } from 'axios';
import { nanoid } from 'nanoid';
import { useMutation } from 'react-query';
import { useSetRecoilState } from 'recoil';

export function useUploadBackgroundImage() {
    const mixpanel = useAnalytics();
    const { isAnonymous, loginWithRedirect } = useAuth();
    const { refetch: refetchBackgroundsList } = useGetBackgroundsList();
    const setModerationError = useSetRecoilState(moderationError);

    const { mutateAsync: mutation } = useMutation(uploadImage, {
        onMutate() {
            mixpanel?.track('upload-image', { event: 'click' });
        },
        async onSuccess() {
            await refetchBackgroundsList();

            mixpanel?.track('upload-image', { event: 'success' });
        },
        onError: async (error: AxiosError<{ kind: string; description?: string }>, { image }: any) => {
            const kind = error?.response?.data?.kind;
            mixpanel?.track('upload-image', { error: kind, event: 'error' });
            let title = 'Failed to upload image';
            let message = '';

            if (kind === ERROR_KINDS.CelebrityDetectedError) {
                setModerationError({ kind: ERROR_KINDS.CelebrityDetectedError, extraData: await localFileToURL(image) });
            } else if (kind === ERROR_KINDS.ImageModerationError) {
                setModerationError({ kind: ERROR_KINDS.ImageModerationError, extraData: await localFileToURL(image) });
            } else {
                let isWarnToast = false;
                if (kind === ERROR_KINDS.InsufficientCreditsError) {
                    isWarnToast = true;
                    title = `Not enough credits to upload an image.`;
                } else if (error?.response?.data?.description) {
                    isWarnToast = true;
                    title = `Image Upload failed`;
                    message = `${error.response.data.description}`;
                } else {
                    logger.error(title, { error });
                }

                const toastMethod = isWarnToast ? toast.info : toast.error;
                toastMethod({ title, message, toastId: `upload-image-error` });
            }
        },
        retry(failureCount: number, error: any) {
            if (failureCount >= 3) {
                return false;
            }
            return !!error.response?.status && (error.response.status === 403 || error.response.status >= 500);
        },
    });

    return async (e: React.MouseEvent<HTMLInputElement>, onEnd?: (e: Event) => void) => {
        if (isAnonymous) {
            mixpanel?.track('go-to-signup', { event: 'click', from: 'upload-image', isAnonymous });
            loginWithRedirect({
                appState: { returnTo: annoynmousUserToEditorPagePostSsoReturnTo },
            });
            e.preventDefault();
            onEnd?.(e.nativeEvent);
        } else {
            e.currentTarget.onchange = async e => {
                const target = e.target as HTMLInputElement;
                const file = target.files?.[0] ?? null;

                if (file) {
                    const { type, size } = file;

                    if (!!type && !['image/png', 'image/jpeg', 'image/jpg'].includes(type)) {
                        toast.info({ title: 'File type not supported', message: 'Please try uploading png, jpeg or jpg files' });
                        onEnd?.(e);
                    } else if (size > maxImageSizeInBytes) {
                        toast.info({ title: `Upload failed: Image must be less than ${bytesToMB(maxImageSizeInBytes, true)}MB` });
                        onEnd?.(e);
                    } else {
                        let finalFile = file;
                        try {
                            finalFile = await removeMetadataAndCreateFile(file, type);
                        } catch {}
                        try {
                            await mutation({
                                image: renameFile(finalFile, `image_${nanoid()}`),
                                metadata: {
                                    presenter_type: ImageType.Backgrounds,
                                },
                            });
                        } finally {
                            target.value = '';
                            onEnd?.(e);
                        }
                    }
                }
            };
        }
    };
}
